import type { BuyerSubmissionDetailDto } from "@/api/buyers";

/**
 * The diligence artifacts Phoenix expects back from a listing broker, and how
 * each one's state is actually recorded.
 *
 * ============================================================================
 * 🔴 THIS LIST IS CODE-RESIDENT, AND DELIBERATELY SO.
 * ============================================================================
 *
 * <p>The obvious-looking alternative is the Portal Document-Request catalogue
 * (`DocumentTypeRef`), which already models "an expected document" as editable
 * rows. <b>It is the wrong table, and wiring this to it would break an
 * unrelated module.</b> Its rows spawn `PreApprovalDocumentRequest` keyed by
 * `DealId`; its 23 seeded items are borrower underwriting artifacts; and it is
 * read LIVE at the moment a Deal enters Pre-Approval. Adding buyer-diligence
 * rows to it would therefore silently change what gets requested of every
 * borrower on every new Deal — a change nobody asked for, visible only to the
 * people it happens to.</p>
 *
 * <p>So the catalogue lives here, in the module that renders it, until somebody
 * needs it admin-editable. <b>If that day comes, the precedent to follow is the
 * Document Language Editor's registry pattern</b> — a registry keyed by a
 * code-resident definition, with overrides layered on top — and NOT the
 * Pre-Approval catalogue.</p>
 *
 * ============================================================================
 * WHAT MAY BE ADDED HERE, AND WHAT MAY NOT
 * ============================================================================
 *
 * <p>🔴 <b>Every item must name a real stamp on the record and a door that can
 * actually set it.</b> An item with neither renders as a row that is
 * permanently outstanding beside a control that does nothing — which the Table
 * and Tile Standard §8.2 names as worse than a read-only row, because it
 * teaches the reader that the surface lies. That is why this list is two items
 * long rather than an aspirational ten: two is how many diligence facts a
 * submission currently carries.</p>
 *
 * <p>The signed LOI is deliberately ABSENT even though `loiAtUtc` and
 * `loiDocumentId` both exist. It is not something the broker sends back — it is
 * what the pairing produces — and it already has two homes: the milestone
 * ladder on Overview reads the date, and the escalation panel is what sets the
 * document. A third surface asserting it would be a third place to disagree.</p>
 *
 * <p><b>No item stores a file, ever.</b> This card tracks RECEIPT; the paper
 * lives in the submission's Documents tab, which is the one place it lives.</p>
 */

/**
 * How an item's state gets recorded — which decides what door its empty row is
 * allowed to carry (Table and Tile Standard §8).
 *
 * <p><b>`stamp`</b> — a specialist asserts the date in place. The row may carry
 * a control, because the control writes the thing it names.</p>
 *
 * <p><b>`derived`</b> — 🔴 the state is computed from something else, so the
 * row must NOT carry a control that appears to write it. The door is the
 * underlying transition, and the row says what that transition is.</p>
 */
export type DiligenceDoorKind = "stamp" | "derived";

export interface DiligenceItem {
  key: string;
  /** The row's name. */
  label: string;
  /** One line saying what the artifact is, for a reader who has not seen one. */
  what: string;
  /** The stamp on the record. `null` / `undefined` → outstanding. */
  receivedAt: (s: BuyerSubmissionDetailDto) => string | null | undefined;
  kind: DiligenceDoorKind;
  /**
   * For a `derived` item: the words naming what ACTUALLY sets it, rendered as
   * prose beside the empty row. Required for `derived` and unused otherwise —
   * a derived row with nothing to say would be an unexplained blank.
   */
  setBy?: string;
}

export const DILIGENCE_ITEMS: readonly DiligenceItem[] = [
  {
    key: "cim",
    label: "CIM",
    what: "The confidential information memorandum, once an NDA is in place.",
    receivedAt: (s) => s.cimReceivedAtUtc,
    // 🔴 DERIVED, and this is the case the standard's warning is written about.
    // `StampMilestoneFor` sets `CimReceivedAtUtc ??= now` when the status moves
    // to "Info received", and deliberately stamps nothing else — its own comment
    // says stamping both documents "would claim documents nobody has seen". So
    // there is no CIM stamp endpoint to call, and a "Mark received" button here
    // would either no-op or write a value the next status move overwrites.
    kind: "derived",
    setBy: "Moving the status to “Info received” stamps this.",
  },
  {
    key: "financials",
    label: "Seller financials",
    what: "The P&L and balance sheet behind the asking price.",
    receivedAt: (s) => s.financialsReceivedAtUtc,
    // A real, independently-settable stamp: "Info received" covers the CIM
    // and/or the financials, and which of the two arrived is the specialist's
    // to record. Forward-only server-side — the date can be corrected, never
    // cleared, because a milestone that has been reached stays reached.
    kind: "stamp",
  },
];
